import * as Auth from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { Amplify } from 'aws-amplify';
import { jwtDecode } from 'jwt-decode';
import { getTenantInfo, getUser, getUserPermissions, getUserPool } from '../api/auth';
import type {
    RecentItem,
    RecentWorkflow,
    TenantInfo,
    UserInfo,
    UserPermission,
    UserPool,
    VinceUserJWTPayload,
    UserSettings
} from '../types/auth';
import { getTenantAlias, getTenantAuthDomain } from '../utils/env';
import { saveUserSettings, updateUserProperty, updateUserProperties, getUserTags } from '../api/users';
import { VL_API_BASE_URL, VL_GRAPHQL_API_BASE_URL } from '../constants/backendUrls';

export const useUserStore = defineStore('UserStore', () => {
    const { $i18n } = useNuxtApp();
    const posthog = usePostHog();

    const isAuthenticated = ref<Boolean | null>(null);
    const userInfo = shallowRef<UserInfo>();

    async function checkSignedInStatus () {
        const session = await Auth.fetchAuthSession();
        isAuthenticated.value = !!session.userSub;
    }

    let _isReadyPromise: Promise<boolean> | null = null;

    function isReady () {
        // If already in progress, return the same promise to avoid duplicate work
        if (_isReadyPromise) {
            return _isReadyPromise;
        }

        // Memoize the promise so concurrent calls share the same execution
        _isReadyPromise = (async () => {
            if (!userPool.value) {
                await fetchUserPool();
            }
            await until(isAuthenticated).not.toBeNull();
            if (isAuthenticated.value) {
                // Wait for userInfo and userPermission to be loaded
                await until(userInfo).toBeTruthy();
                await until(userPermission).toBeTruthy();
            }
            return true;
        })();

        return _isReadyPromise;
    }

    const userPool = ref<UserPool | null>(null);
    const signInUserEmail = ref<string>('');

    async function fetchUserPool () {
        const tenantAlias = getTenantAlias();
        let pool = await getUserPool(tenantAlias);
        pool = Object.assign(pool, {
            userPoolId: pool.userPoolId,
            userPoolClientId: pool.userPoolClientId,
            mandatorySignIn: true,
            loginWith: {
                oauth: {
                    domain: getTenantAuthDomain(pool),
                    scopes: ['email', 'openid', 'profile'],
                    redirectSignIn: [`${window.location.origin}/auth/oauth2/`],
                    redirectSignOut: [],
                    responseType: 'code',
                    providers: pool.ssoProviders
                }
            }
        });

        Amplify.configure({
            Auth: { Cognito: pool },
            API: {
                REST: {
                    [VL_API_BASE_URL]: {
                        endpoint: VL_API_BASE_URL
                    }
                },
                GraphQL: {
                    endpoint: VL_GRAPHQL_API_BASE_URL + '/graphql',
                    defaultAuthMode: 'userPool'
                }
            }
        });
        userPool.value = pool;
    }

    watch(userPool, () => {
        checkSignedInStatus();
    });

    async function signIn (username: string, password: string) {
        try {
            await signOut();
        } catch (ignore) { /* empty */
        }

        if (!userPool.value) {
            await fetchUserPool();
        }
        const { isSignedIn, nextStep } = await Auth.signIn({ username, password });
        isAuthenticated.value = isSignedIn;
        return { isSignedIn, nextStep };
    }
    async function completeNewPassword (newPassword: string) {
        if (!userPool.value) {
            await fetchUserPool();
        }
        const { isSignedIn, nextStep } = await Auth.confirmSignIn({ challengeResponse: newPassword });
        isAuthenticated.value = true;
        return { isSignedIn, nextStep };

    }

    async function confirmSignInWithCode (code: string) {
        if (!userPool.value) {
            await fetchUserPool();
        }
        const { isSignedIn, nextStep } = await Auth.confirmSignIn({ challengeResponse: code });
        isAuthenticated.value = isSignedIn;
        return { isSignedIn, nextStep };
    }

    const refreshUserTags = async () => {
        if (!userInfo.value?.userId) { return; }
        try {
            const response = await getUserTags(userInfo.value.userId);
            userInfo.value = { ...userInfo.value, tags: response.data };
        } catch (e) {
            console.error('[userStore][refreshUserTags] error', e);
        }
    };

    async function forgotPassword (username: string) {
        if (!userPool.value) {
            await fetchUserPool();
        }
        const response = await Auth.resetPassword({ username });
        return response;
    }

    async function confirmResetPassword (username: string, newPassword: string, confirmationCode: string) {
        if (!userPool.value) {
            await fetchUserPool();
        }
        return await Auth.confirmResetPassword({ username, newPassword, confirmationCode });
    }

    const userPermission = shallowRef<UserPermission>();
    const tenantInfo = shallowRef<TenantInfo>();
    const userPerPageRecords = ref<number>(10);
    watch(isAuthenticated, async (value) => {
        // Reset isReady cache when authentication state changes
        _isReadyPromise = null;

        if (!isAuthenticated.value) {
            return;
        }
        tenantInfo.value = await getTenantInfo();
        userPermission.value = await getUserPermissions();
        if (value) {
            try {
                const decoded = jwtDecode((await getJWT())!) as VinceUserJWTPayload;
                userInfo.value = {
                    ...await getUser(),
                    groups: decoded['cognito:groups'],
                    nickname: decoded.nickname,
                    username: decoded.sub!,
                    isVinceLiveAdmin: decoded['cognito:groups'].includes('VinceLiveAdmin'),
                    isVinceLiveUser: decoded['cognito:groups'].includes('VinceLiveUser'),
                    isTenantAdmin: decoded['cognito:groups'].includes('TenantAdmin') || decoded['cognito:groups'].includes('VinceLiveAdmin'),
                    tenantType: tenantInfo.value.tenantType
                } as UserInfo;

                userPerPageRecords.value = userInfo.value?.userSettings?.defaultNumberOfRecords || 10;
                $i18n.setLocale(userInfo.value?.userSettings?.preferredLocale);

                // Identify user with posthog
                posthog?.identify(userInfo.value!.userId, {
                    user_id: userInfo.value!.userId,
                    tenant_name: tenantInfo.value.tenantName,
                    tenant_id: tenantInfo.value.tenantId
                });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
            }
        }
    });

    Hub.listen('auth', ({ payload }) => {
        const { event } = payload;
        isAuthenticated.value = ['signedIn', 'tokenRefresh', 'signInWithRedirect'].includes(event);
    });

    async function getJWT (forceRefresh: boolean = false) {
        const request = forceRefresh ? { forceRefresh: true } : undefined;
        const session = await Auth.fetchAuthSession(request);
        return session.tokens?.idToken?.toString();
    }

    async function signOut () {
        if (!userPool.value) {
            await fetchUserPool();
        }
        await Auth.signOut();
        posthog?.reset();
        // _isReady = false;
    }

    function tenantId () {
        return userInfo.value!.tenantId;
    }

    function permission () {
        return userPermission.value;
    }

    const updateUserSettings = async (data: UserSettings) => {
        const payload = {
            tenantId: userInfo.value!.tenantId,
            userId: userInfo.value!.userId,
            userSettings: data
        };
        try {
            await saveUserSettings(payload);
            userInfo.value = { ...userInfo.value!, userSettings: data };
            userPerPageRecords.value = data?.defaultNumberOfRecords || 10;
            $i18n.setLocale(data?.preferredLocale);
        } catch (err) {
            throw new Error(err.response.data);
        }
    };

    const updateUserDetails = async (properties: Partial<UserInfo>) => {
        try {
            await updateUserProperties(properties, userInfo.value!.userId, tenantId());
            userInfo.value = { ...userInfo.value!, ...properties };
        } catch (err) {
            throw new Error(err.response?.data || err);
        }
    };

    // TODO further generify
    async function addFavoriteItem (workflowId: string, type: string = 'workflow'): Promise<boolean> {
        if (type === 'workflow' && !userInfo.value!.favoriteWorkflows?.includes(workflowId)) {
            if (!userInfo.value!.favoriteWorkflows) {
                userInfo.value!.favoriteWorkflows = [];
            }
            userInfo.value!.favoriteWorkflows.push(workflowId);
            await updateUserProperty([workflowId], 'favoriteWorkflows', userInfo.value!.userId!, tenantId());
            return true;
        }
        return false;
    }

    async function removeFavoriteItem (workflowId: string, type: string = 'workflow'): Promise<boolean> {
        if (type === 'workflow' && userInfo.value!.favoriteWorkflows?.includes(workflowId)) {
            userInfo.value!.favoriteWorkflows = userInfo.value!.favoriteWorkflows.filter(it => it !== workflowId);
            await updateUserProperty([workflowId], 'favoriteWorkflows', userInfo.value!.userId!, tenantId(), true);
            return true;
        }
        return false;
    }

    async function addRecentItem (workflowId: string, type: string = 'workflow') {
        if (userInfo.value && type === 'workflow') {
            const filteredRecents = (userInfo.value!.recentWorkflows ?? []).filter(it => it.workflowId !== workflowId);
            filteredRecents.splice(0, 0, { workflowId, lastAccessedTime: new Date().toISOString() });
            await updateUserProperty(filteredRecents.slice(0, 9), 'recentWorkflows', userInfo.value!.userId!, tenantId());

            userInfo.value = { ...userInfo.value!, recentWorkflows: filteredRecents };
            return true;
        }
        return false;
    }

    async function removeRecentItem (workflowId: string, type: string = 'workflow') {
        if (type === 'workflow') {
            // if (userInfo.value!.favoriteWorkflows)
            // userInfo.value!.
            await console.log(workflowId);
        }
    }

    function getRecentItems (type: string = 'workflow'): RecentItem[] {
        if (type === 'workflow' && userInfo.value?.recentWorkflows) {
            return userInfo.value!.recentWorkflows?.map((it: RecentWorkflow) => {
                return { id: it.workflowId, lastAccessedTime: it.lastAccessedTime, type };
            });
        } else {
            return [];
        }
    }

    const signInWithSso = async (providerName: string, redirect?: string) => {
        if (!userPool.value) {
            await fetchUserPool();
        }
        await Auth.signInWithRedirect({
            provider: { custom: providerName },
            customState: redirect && redirect !== '/' ? redirect : undefined
        });
    };

    return {
        tenantId,
        userPool,
        fetchUserPool,
        signIn,
        isAuthenticated,
        fetchAuthSession: Auth.fetchAuthSession,
        isReady,
        getJWT,
        userInfo,
        userPermission,
        permission,
        updateUserSettings,
        updateUserDetails,
        signOut,
        tenantInfo,
        formatDateTime,
        formatDateTimePassed,
        addFavoriteItem,
        removeFavoriteItem,
        addRecentItem,
        removeRecentItem,
        getRecentItems,
        forgotPassword,
        confirmResetPassword,
        signInUserEmail,
        userPerPageRecords,
        signInWithSso,
        completeNewPassword,
        confirmSignInWithCode,
        refreshUserTags
    };
});
